import { createElement, useState } from "react";
import { Trans } from "react-i18next";
import { post, put } from "../../../common/api/apiShared";
import { Result } from "../../../common/api/result";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { isNullOrWhitespace } from "../../../common/stringOperations";
import { useTranslation } from "../../../localization/useTranslation";
import { DataPrivacyClassification } from "../../../processingActivity/DataCategoriesStep/DataCategoriesStep.types";
import { DataCategorySaveModel } from "../dataCategories/DataCategories.types";
import { IAddEditDataCategoryDialog } from "./AddEditDataCategoryDialog";
import { DataType } from "../dataTypes/DataTypesList.types.ts";
import { useDotLegalQuery, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useAddEditDataCategoryDialogHooks(props: IAddEditDataCategoryDialog) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const [errors, setErrors] = useState({ nameError: "", classificationError: "" });
    const [saveModel, setSaveModel] = useState<DataCategorySaveModel>(
        props.dataCategory ?? {
            dataTypes: [],
            isCustom: true,
            name: "",
            active: true,
        }
    );
    const url = "/DataTypes";
    const { isLoading: isLoadingDataTypes, data: dataTypes } = useDotLegalQuery<Array<DataType>>(url);

    function save() {
        return post<string>("/DataCategories/", saveModel);
    }

    function update(id: string) {
        return put<string>(`/DataCategories/${id}/`, saveModel);
    }

    const saveMutation = useDotLegalMutation(save, {
        onSuccess: (response: Result<string>) => {
            if (response.success()) {
                props.onDialogClose();
                props.refetch();
                props.onOkClick(response.value(), saveModel.name);
                snackbar.show(createElement(Trans, { i18nKey: "dataCategoryCreated", values: { name: saveModel.name } }));
            } else {
                if (response.hasError("nameAlreadyExists")) {
                    snackbar.show(translateString("dataCategoryExist"), "warning");
                }
            }
        },
    });

    const createDataCategory = async () => {
        await saveMutation.mutateAsync();
    };

    const editMutation = useDotLegalMutation(update);
    const updateDataCategory = () => {
        editMutation.mutateAsync(saveModel.id!, {
            onSuccess: (response: Result<string>) => {
                if (response.success()) {
                    snackbar.show(translateString("dataCategoryUpdated", { name: saveModel.name }));
                    props.onDialogClose();
                    props.refetch();
                } else {
                    if (response.hasError("nameAlreadyExists")) {
                        snackbar.show(translateString("dataCategoryExist"), "warning");
                    }
                    if (response.hasError("dataTypesInUse")) {
                        snackbar.show(translateString("dataTypeInUseOnProcessingActivity"), "warning");
                        props.onDialogClose();
                    }
                }
            },
        });
    };

    const onDataTypesChanged = (dataTypes: Array<string>) => {
        let tempSaveModel = { ...saveModel };
        tempSaveModel.dataTypes = dataTypes;
        setSaveModel(tempSaveModel);
    };

    return {
        viewModel: saveModel,
        setViewModel: setSaveModel,
        getDataPrivacyClassificationOptions,
        validateDataCategoryName,
        validateDataCategoryType,
        validateDataCategoryForm,
        errors,
        isSaving: editMutation.isLoading || saveMutation.isLoading,
        onDataTypesChanged,
        dataTypes,
        isLoadingDataTypes,
    };

    function getDataPrivacyClassificationOptions() {
        return [
            { name: translateString("none"), id: DataPrivacyClassification.None.toString() },
            { name: translateString("ordinary"), id: DataPrivacyClassification.Ordinary.toString() },
            { name: translateString("sensitive"), id: DataPrivacyClassification.Sensitive.toString() },
        ];
    }

    function validateDataCategoryName(name: string) {
        let tempErrors = { ...errors };

        tempErrors.nameError = isNullOrWhitespace(name) ? translateString("fieldRequired") : "";

        setErrors(tempErrors);
    }

    function validateDataCategoryType(type: DataPrivacyClassification | undefined) {
        let tempErrors = { ...errors };

        tempErrors.classificationError = type ? "" : translateString("fieldRequired");

        setErrors(tempErrors);
    }

    function validateDataCategoryForm() {
        let formValid = true;
        let tempErrors = { ...errors };

        if (isNullOrWhitespace(saveModel?.name)) {
            tempErrors.nameError = translateString("fieldRequired");
            formValid = false;
        }

        if (saveModel?.classification === undefined) {
            tempErrors.classificationError = translateString("fieldRequired");
            formValid = false;
        }

        if (formValid) {
            if (props.isAdd) createDataCategory();
            else updateDataCategory();
        } else {
            setErrors(tempErrors);
        }
    }
}
